import { useEffect, useMemo, useState } from 'react'
import { useStore } from '@/store'
import { authFetch } from '@/auth/authFetch'
import { API_BASE_URL } from '@/analysis/endpoints'
import { useHasPermission } from '@/auth/hooks'
import { AmplifyChip } from '@/components/amplify/AmplifyPanel'
import { getRegionLabel } from '@/constants'

type HealthBody = { ok?: boolean; db?: string; message?: string }
type AdminStatus = {
  kafka?: { brokersConfigured?: boolean }
  amplify?: { allowed?: boolean; error?: string; message?: string } | null
}

export default function Topbar() {
  const view = useStore((s) => s.view)
  const drillRegion = useStore((s) => s.drillRegion)
  const drillStream = useStore((s) => s.drillStream)
  const drillNode = useStore((s) => s.drillNode)
  const timeMode = useStore((s) => s.timeMode)
  const nowTs = useStore((s) => s.nowTs)
  const lastDataError = useStore((s) => s.lastDataError)
  const setView = useStore((s) => s.setView)
  const clearDrill = useStore((s) => s.clearDrill)
  const clearDrillNode = useStore((s) => s.clearDrillNode)
  const drillToRegion = useStore((s) => s.drillToRegion)
  const toggleSidebar = useStore((s) => s.toggleSidebar)
  const sidebarOpen = useStore((s) => s.sidebarOpen)
  const isAdmin = useHasPermission('user:manage')

  const [health, setHealth] = useState<HealthBody | null>(null)
  const [admin, setAdmin] = useState<AdminStatus | null>(null)

  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/health`)
        const body = await res.json().catch(() => ({})) as HealthBody
        if (!cancelled) setHealth({ ok: res.ok && body.ok !== false, db: body.db, message: body.message })
      } catch (e) {
        if (!cancelled) setHealth({ ok: false, message: String((e as Error).message ?? e) })
      }
      if (isAdmin) {
        try {
          const res = await authFetch(`${API_BASE_URL}/api/admin/status`)
          if (res.ok) {
            const body = await res.json() as AdminStatus
            if (!cancelled) setAdmin(body)
          }
        } catch {
          /* ignore — health pip still works from /health */
        }
      }
    }
    void poll()
    const t = setInterval(() => void poll(), 30_000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [isAdmin])

  const status = useMemo(() => {
    const issues: string[] = []
    if (health && health.ok === false) issues.push(`API/DB: ${health.message || health.db || 'down'}`)
    if (lastDataError) issues.push(`Data: ${lastDataError}`)
    if (admin?.kafka && admin.kafka.brokersConfigured === false) issues.push('Kafka: brokers not configured')
    if (admin?.amplify?.error) issues.push(`Amplify: ${admin.amplify.message || admin.amplify.error}`)
    const ok = issues.length === 0 && (health?.ok !== false)
    const title = ok
      ? 'All services appear healthy'
      : `Issues:\n${issues.join('\n')}`
    return { ok, title }
  }, [health, admin, lastDataError])

  const viewLabels: Record<string, string> = {
    world: 'World view', hex: 'Hex grid', deep: 'Deep dive',
    incidents: 'Incidents', reports: 'Reports', users: 'Users', settings: 'Settings',
    streams: 'Streams',
  }

  const isLive = timeMode === 'live'

  const goBack = () => {
    if (view === 'deep') {
      useStore.setState({ view: 'hex', drillStream: null })
      return
    }
    if (view === 'hex') {
      if (drillNode || drillRegion) {
        clearDrillNode()
        return
      }
      setView('world')
      return
    }
    clearDrill()
    setView('world')
  }

  const drillNodeLabel = drillNode
    ? `${drillNode.dimension}: ${getRegionLabel(drillNode.value) || drillNode.value}`
    : null

  return (
    <div style={{
      background: 'var(--bg-1)', borderBottom: '0.5px solid var(--bd-0)',
      padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 7,
      backdropFilter: 'blur(12px)', flexShrink: 0,
    }}>
      <button
        onClick={toggleSidebar}
        aria-label="Toggle navigation"
        style={{
          width: 28, height: 28,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8,
          border: 'none',
          background: 'transparent',
          color: 'var(--t-1)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <line x1="3" y1="4" x2="13" y2="4" />
          <line x1="3" y1="8" x2="13" y2="8" />
          <line x1="3" y1="12" x2="13" y2="12" />
        </svg>
      </button>

      <div
        onClick={() => { clearDrill(); setView('world') }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
          padding: '2px 6px',
          borderRadius: 8,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        aria-label="Home"
      >
        <div style={{
          width: 24, height: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width={24} height={24}>
            <path
              d="M 50 8 L 86.37 29 L 86.37 71 L 50 92 L 13.63 71 L 13.63 29 Z"
              fill="#14b8a6"
              fillOpacity="0.9"
              stroke="#5eead4"
              strokeWidth="1"
              strokeOpacity="0.95"
            />
            <path
              d="M 50 25 L 71.65 37.5 L 71.65 62.5 L 50 75 L 28.35 62.5 L 28.35 37.5 Z"
              fill="#5eead4"
              fillOpacity="0.95"
            />
          </svg>
        </div>
        {sidebarOpen && (
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--t-0)', letterSpacing: '-0.01em' }}>
            Metrics System
          </span>
        )}
      </div>

      {view !== 'world' && (
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          title={
            view === 'deep'
              ? 'Back to Hex'
              : view === 'hex' && (drillNode || drillRegion)
                ? 'Widen geo scope'
                : 'Back to World'
          }
          style={{
            width: 28, height: 28,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8,
            border: '0.5px solid var(--bd-1)',
            background: 'var(--bg-2)',
            color: 'var(--t-1)',
            cursor: 'pointer',
            flexShrink: 0,
            padding: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3 L5 8 L10 13" />
          </svg>
        </button>
      )}

      <span style={{ fontSize: 12, color: 'var(--t-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {viewLabels[view] || view}
      </span>

      {drillNodeLabel && (
        <>
          <span style={{ color: 'var(--t-3)', fontSize: 11 }}>›</span>
          <span
            style={{
              fontSize: 12,
              color: drillStream ? 'var(--t-2)' : 'var(--t-0)',
              cursor: drillStream ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (!drillStream) return
              useStore.setState({ drillStream: null, view: 'hex' })
            }}
            title={drillStream ? 'Clear stream focus' : undefined}
          >
            {drillNodeLabel}
          </span>
        </>
      )}

      {drillRegion && !drillNode && (
        <>
          <span style={{ color: 'var(--t-3)', fontSize: 11 }}>›</span>
          <span
            style={{ fontSize: 12, color: drillStream ? 'var(--t-2)' : 'var(--t-0)', cursor: drillStream ? 'pointer' : 'default' }}
            onClick={() => drillStream ? drillToRegion(drillRegion) : undefined}
          >{drillRegion} region</span>
        </>
      )}

      {drillStream && (
        <>
          <span style={{ color: 'var(--t-3)', fontSize: 11 }}>›</span>
          <span style={{ fontSize: 12, color: 'var(--t-0)' }}>{drillStream}</span>
        </>
      )}

      <div style={{ flex: 1 }} />

      <AmplifyChip />

      <div
        className="live-pip"
        title={status.title}
        aria-label={status.title}
        style={{
          background: status.ok ? 'var(--ok)' : 'var(--crit)',
          opacity: isLive ? 1 : 0.55,
        }}
      />
      <span style={{ fontSize: 11, color: 'var(--t-1)', fontFamily: 'var(--font-mono)' }}>
        {new Date(nowTs).toLocaleTimeString('en-GB')}
      </span>
    </div>
  )
}
